/** SEO head for the Agents page (`/agents`) — rendered into the SSR HTML by the route's `head()`; kept in sync on SPA navigation. */

import { isProduction } from "@/config/config";
import { PUBLISHER_NODE, SITE_URL, WEBSITE_NODE } from "@/lib/marketing-jsonld";

// Derived from SITE_URL (which carries the trailing slash) so one module
// holds a single source for the origin (PR #899 review).
export const AGENTS_URL = `${SITE_URL}agents`;

export const AGENTS_DOCUMENT_TITLE =
  "GoodSender AI tools: MCP, CLI, and Skills for your blog";

/** Keep search cards aligned with the visible connection/workflow distinction. */
export const AGENTS_DESCRIPTION =
  "Connect Claude, Codex, or another assistant with MCP or CLI. Add Skills for editorial work. Choose destinations and review before publishing or sending.";

const AGENTS_TWITTER_DESCRIPTION =
  "Claude or Codex, your direction. MCP and CLI connect to GoodSender. Skills guides the workflow. Review your draft and selected destinations before publishing or sending.";

const AGENTS_OG_IMAGE = "https://goodsender.com/og-image.png";

export const agentsJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${AGENTS_URL}#agents`,
  url: AGENTS_URL,
  name: AGENTS_DOCUMENT_TITLE,
  description: AGENTS_DESCRIPTION,
  inLanguage: "en",
  isPartOf: WEBSITE_NODE,
  publisher: PUBLISHER_NODE,
};

export const agentsBreadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
    { "@type": "ListItem", position: 2, name: "AI tools", item: AGENTS_URL },
  ],
};

type HeadElement = { type: "meta" | "link"; props: Record<string, string> };

const AGENTS_SEO_HEAD_ELEMENTS: HeadElement[] = [
  { type: "meta", props: { name: "description", content: AGENTS_DESCRIPTION } },
  {
    type: "meta",
    props: {
      name: "robots",
      content: isProduction
        ? "index, follow, max-image-preview:large"
        : "noindex, nofollow",
    },
  },
  { type: "meta", props: { name: "author", content: "Joy Labs Ventures" } },
  { type: "link", props: { rel: "canonical", href: AGENTS_URL } },
  // Open Graph
  { type: "meta", props: { property: "og:type", content: "website" } },
  { type: "meta", props: { property: "og:url", content: AGENTS_URL } },
  { type: "meta", props: { property: "og:title", content: AGENTS_DOCUMENT_TITLE } },
  { type: "meta", props: { property: "og:description", content: AGENTS_DESCRIPTION } },
  { type: "meta", props: { property: "og:image", content: AGENTS_OG_IMAGE } },
  { type: "meta", props: { property: "og:image:width", content: "1200" } },
  { type: "meta", props: { property: "og:image:height", content: "630" } },
  { type: "meta", props: { property: "og:site_name", content: "GoodSender" } },
  { type: "meta", props: { property: "og:locale", content: "en_US" } },
  // Twitter Card
  { type: "meta", props: { name: "twitter:card", content: "summary_large_image" } },
  { type: "meta", props: { name: "twitter:title", content: AGENTS_DOCUMENT_TITLE } },
  {
    type: "meta",
    props: { name: "twitter:description", content: AGENTS_TWITTER_DESCRIPTION },
  },
  { type: "meta", props: { name: "twitter:image", content: AGENTS_OG_IMAGE } },
  // AEO / LLM discoverability
  {
    type: "link",
    props: {
      rel: "alternate",
      type: "text/plain",
      href: "https://goodsender.com/llms.txt",
      title: "LLM Summary",
    },
  },
  {
    type: "link",
    props: {
      rel: "alternate",
      type: "text/plain",
      href: "https://goodsender.com/llms-full.txt",
      title: "LLM Full Reference",
    },
  },
  {
    type: "link",
    props: {
      rel: "describedby",
      type: "application/json",
      href: "https://goodsender.com/api/openapi.json",
    },
  },
];

/** Route-level `head()` for /agents (same contract as the other marketing heads). */
export function agentsRouteHead() {
  return {
    meta: [
      { title: AGENTS_DOCUMENT_TITLE },
      ...AGENTS_SEO_HEAD_ELEMENTS.filter((e) => e.type === "meta").map((e) => e.props),
    ],
    links: AGENTS_SEO_HEAD_ELEMENTS.filter((e) => e.type === "link").map((e) => e.props),
  };
}
